import PageHero from "@/components/PageHero";
import SEO from "@/components/SEO";
import { motion } from "framer-motion";
import { Fingerprint, Users, Send, PartyPopper } from "lucide-react";
import { useTranslation } from "react-i18next";
import WaitlistCTA from "@/components/home/WaitlistCTA";

const STEP_ICONS = [Fingerprint, Users, Send, PartyPopper];

export default function HowItWorks() {
  const { t } = useTranslation();
  const STEPS = (t("content.howItWorks.steps", { returnObjects: true }) || []).map((s, i) => ({
    n: String(i + 1).padStart(2, "0"),
    icon: STEP_ICONS[i] || Send,
    title: s.title,
    body: s.body,
    detail: s.detail || [],
  }));
  return (
    <>
      <SEO
        slug="how-it-works"
        title="How it works · Send in under two minutes — Instantxfer CashApp"
        description="A four-tap transfer: verify in 90 seconds, add your people, send with the exact number, watch it land. Cash pickup, mobile money, bank or scan-to-pay."
      />
      <PageHero
        eyebrow={t("pageHero.howItWorks.eyebrow")}
        title={t("pageHero.howItWorks.title")}
        italic={t("pageHero.howItWorks.italic")}
        body={t("pageHero.howItWorks.body")}
        testId="hiw-hero"
      />
      <section className="pb-24 md:pb-32 bg-canvas">
        <div className="container-x space-y-6">
          {STEPS.map((s, i) => (
            <motion.div
              key={s.n}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: (i % 3) * 0.05 }}
              className={`card-flat p-8 md:p-12 grid grid-cols-12 gap-6 items-start ${
                i === 0 ? "!bg-forest text-canvas !border-forest" : ""
              }`}
              data-testid={`hiw-step-${s.n}`}
            >
              <div className="col-span-12 md:col-span-2">
                <div className={`font-serif italic text-6xl md:text-7xl leading-none ${i === 0 ? "text-lime" : "text-forest/85"}`}>
                  {s.n}
                </div>
              </div>
              <div className="col-span-12 md:col-span-6">
                <div className={`w-11 h-11 rounded-full grid place-items-center mb-4 ${
                  i === 0 ? "bg-lime text-ink" : "bg-forest text-canvas"
                }`}>
                  <s.icon className="w-5 h-5" strokeWidth={1.75} />
                </div>
                <h3 className={`font-display font-medium text-3xl md:text-4xl tracking-tight ${i === 0 ? "text-canvas" : "text-forest"}`}>
                  {s.title}
                </h3>
                <p className={`mt-4 leading-relaxed ${i === 0 ? "text-canvas/80" : "text-ink/70"}`}>{s.body}</p>
              </div>
              <div className="col-span-12 md:col-span-4">
                <ul className="space-y-3">
                  {s.detail.map((d) => (
                    <li key={d} className={`flex items-start gap-3 text-sm ${i === 0 ? "text-canvas/85" : "text-ink/70"}`}>
                      <span className={`mt-1 w-1.5 h-1.5 rounded-full ${i === 0 ? "bg-lime" : "bg-forest"}`} />
                      {d}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </div>
      </section>
      <WaitlistCTA />
    </>
  );
}
